/**
 * ScarcityNudge · iter140
 *
 * Live-Kauf-Signal für Unikat-Listings (STX/MP quantity=1).
 * Public-Endpoint: GET /api/cart/scarcity/{listing_id}
 * Zeigt:
 *  - "In X anderen Warenkörben — sichere dir jetzt" (Konversions-Nudge)
 *  - "Aktuell reserviert (MM:SS)" wenn jemand anderes im Checkout ist
 *
 * Wird nur angezeigt wenn is_unique_stock=true.
 */
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Users, Lock, Clock } from "lucide-react";
import api from "@/lib/api";

function fmtMMSS(sec) {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export default function ScarcityNudge({ listingId, theme = "default" }) {
  const { t } = useTranslation();
  const [signal, setSignal] = useState(null);
  const [remaining, setRemaining] = useState(null);

  const load = useCallback(async () => {
    if (!listingId) return;
    try {
      const { data } = await api.get(`/cart/scarcity/${listingId}`);
      setSignal(data);
      if (data?.locked_by_other && data?.lock_expires_at) {
        const diff = Math.floor((new Date(data.lock_expires_at).getTime() - Date.now()) / 1000);
        setRemaining(diff > 0 ? diff : null);
      } else {
        setRemaining(null);
      }
    } catch (e) { /* stille Ignore — Nudge ist optional */ }
  }, [listingId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 20000); // alle 20s refreshen
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    if (remaining === null) return undefined;
    const iv = setInterval(() => setRemaining((s) => (s === null ? null : Math.max(0, s - 1))), 1000);
    return () => clearInterval(iv);
  }, [remaining]);

  if (!signal || !signal.is_unique_stock) return null;

  const dark = theme === "stx";
  const wrapper = dark
    ? "rounded-2xl border border-stx-border bg-stx-surface"
    : "rounded-2xl border border-[#E5E5E5] bg-white";

  // Priorität 1: Locked-by-Other (aktuell reserviert)
  if (signal.locked_by_other) {
    return (
      <div data-testid={`scarcity-nudge-${listingId}`} className={`${wrapper} p-3 flex items-center gap-3`}>
        <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center shrink-0">
          <Lock size={16} className="text-red-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold ${dark ? "text-stx-text" : "text-[#0A0A0A]"}`}>
            {t("scarcity.locked_title", { defaultValue: "Gerade reserviert" })}
          </p>
          <p className={`text-xs ${dark ? "text-stx-text-muted" : "text-[#666]"}`}>
            {t("scarcity.locked_body", { defaultValue: "Ein anderer Käufer prüft dieses Unikat gerade an der Kasse." })}
          </p>
        </div>
        {remaining !== null && (
          <span data-testid={`scarcity-lock-timer-${listingId}`} className="font-mono font-bold text-red-700 tabular-nums text-sm shrink-0">
            <Clock size={12} className="inline mr-1" /> {fmtMMSS(remaining)}
          </span>
        )}
      </div>
    );
  }

  // Priorität 2: Scarcity-Nudge — in mehreren Warenkörben
  if (signal.in_other_carts > 0) {
    return (
      <div data-testid={`scarcity-nudge-${listingId}`} className={`${wrapper} p-3 flex items-center gap-3`}>
        <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
          <Users size={16} className="text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold ${dark ? "text-stx-text" : "text-[#0A0A0A]"}`}>
            {t("scarcity.hot_title", {
              count: signal.in_other_carts,
              defaultValue: "Aktuell in {{count}} anderen Warenkörben",
            })}
          </p>
          <p className={`text-xs ${dark ? "text-stx-text-muted" : "text-[#666]"}`}>
            {t("scarcity.hot_body", { defaultValue: "Es ist ein Unikat — sichere es dir jetzt, bevor jemand anderes zur Kasse geht." })}
          </p>
        </div>
      </div>
    );
  }

  // Priorität 3: Ruhiges „Unikat"-Signal (nichts anders in Cart, kein Lock)
  return (
    <div data-testid={`scarcity-nudge-${listingId}`} className={`${wrapper} p-3 flex items-center gap-3`}>
      <div className={`w-9 h-9 rounded-full ${dark ? "bg-stx-accent/15" : "bg-emerald-50"} flex items-center justify-center shrink-0`}>
        <Users size={16} className={dark ? "text-stx-accent" : "text-emerald-600"} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-bold ${dark ? "text-stx-text" : "text-[#0A0A0A]"}`}>
          {t("scarcity.unique_title", { defaultValue: "Unikat — nur einmal verfügbar" })}
        </p>
        <p className={`text-xs ${dark ? "text-stx-text-muted" : "text-[#666]"}`}>
          {t("scarcity.unique_body", { defaultValue: "Sobald jemand zur Kasse geht, wird es für 20 Minuten reserviert." })}
        </p>
      </div>
    </div>
  );
}
